import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import TimelineIcon from '@material-ui/icons/Timeline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import { useAuth } from './auth/AuthContext';
import useSharedStyles from './sharedStyles';
import { NodeList } from './NodeLists';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1200,
    margin: '0 auto',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  card: {
    padding: theme.spacing(2),
    background: '#111',
    color: '#fff',
  },
  title: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  subtle: {
    opacity: 0.85,
  },
  tableWrap: {
    marginTop: theme.spacing(2),
    overflowX: 'auto',
  },
  table: {
    minWidth: 900,
    background: '#111',
  },
  th: {
    color: '#fff',
    fontWeight: 800,
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  },
  td: {
    color: '#fff',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  error: {
    color: '#ff7777',
    whiteSpace: 'pre-wrap',
    marginTop: theme.spacing(1),
  },
  success: {
    color: '#7CFC90',
    whiteSpace: 'pre-wrap',
    marginTop: theme.spacing(1),
  },
  artifactCard: {
    padding: theme.spacing(1.5),
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10,
    marginTop: theme.spacing(1),
  },
  actionButton: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.22)',
    '&:hover': {
      borderColor: '#fff',
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.35)',
      borderColor: 'rgba(255,255,255,0.12)',
    },
  },
  kv: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    opacity: 0.92,
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  toolbarRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
}));

function tierFromRoles(roles) {
  const r = Array.isArray(roles) ? roles : [];
  if (r.includes('enterprise')) return 'enterprise';
  if (r.includes('premium')) return 'premium';
  return 'free';
}

function getPlanLimits(tier) {
  if (tier === 'enterprise') {
    return { uploadsPerMonth: 1000, maxBytes: 500 * 1024 * 1024, maxRows: 2_000_000 };
  }
  if (tier === 'premium') {
    return { uploadsPerMonth: 10, maxBytes: 100 * 1024 * 1024, maxRows: 300_000 };
  }
  return { uploadsPerMonth: 3, maxBytes: 30 * 1024 * 1024, maxRows: 100_000 };
}

function axiosErrMsg(e) {
  try {
    if (e && e.response && e.response.data && e.response.data.message) {
      return String(e.response.data.message);
    }
    const parts = [];
    if (e && e.message) parts.push(String(e.message));
    if (e && e.code) parts.push(`code=${String(e.code)}`);
    if (e && e.config && e.config.url) parts.push(`url=${String(e.config.url)}`);
    return parts.length ? parts.join(' ') : String(e);
  } catch {
    return 'Request failed';
  }
}

function startOfMonthUtcMs() {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0);
}

export default function PcapUploadsPage() {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const navigate = useNavigate();
  const { isAuthenticated, roles, authChecked } = useAuth();

  const tier = useMemo(() => tierFromRoles(roles), [roles]);
  const plan = useMemo(() => getPlanLimits(tier), [tier]);

  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const [searchText, setSearchText] = useState('');

  const [selectedUploadIds, setSelectedUploadIds] = useState(() => new Set());
  const [bulkArtifacts, setBulkArtifacts] = useState(() => ({}));

  const usedThisMonth = useMemo(() => {
    const cutoff = startOfMonthUtcMs();
    const statuses = new Set(['queued', 'ingesting', 'ready', 'stories_running', 'thesis_running', 'done']);
    let c = 0;
    for (const it of (Array.isArray(items) ? items : [])) {
      if (!it) continue;
      const ts = it.created_at ? Date.parse(String(it.created_at)) : NaN;
      if (!Number.isFinite(ts) || ts < cutoff) continue;
      const st = it.status ? String(it.status) : '';
      if (!statuses.has(st)) continue;
      c += 1;
    }
    return c;
  }, [items]);

  const promptLogin = useCallback(() => {
    try {
      if (window && typeof window.globalShowLogin === 'function') {
        window.globalShowLogin(true);
        return;
      }
    } catch (e) {
    }
    navigate('/login');
  }, [navigate]);

  const loadUploads = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${SERVER_NAME}/pcap/uploads`, {
        params: { limit: 200, offset: 0 },
        withCredentials: true,
        timeout: 20000,
      });
      const next = resp && resp.data && Array.isArray(resp.data.items) ? resp.data.items : [];
      setItems(next);
    } catch (e) {
      setError(axiosErrMsg(e));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated) return;
    loadUploads();
  }, [authChecked, isAuthenticated, loadUploads]);

  const onPickFile = useCallback(() => {
    setError('');
    setOkMsg('');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  const toggleAllSelected = useCallback((selected) => {
    setSelectedUploadIds(() => {
      if (!selected) return new Set();
      const next = new Set();
      (Array.isArray(items) ? items : []).forEach((it) => {
        const id = it && it.upload_id ? String(it.upload_id).trim() : '';
        if (id) next.add(id);
      });
      return next;
    });
  }, [items]);

  const toggleSelected = useCallback((uploadId) => {
    const id = String(uploadId || '').trim();
    if (!id) return;
    setSelectedUploadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setAllSelected = useCallback((selected) => {
    setSelectedUploadIds(() => {
      if (!selected) return new Set();
      const next = new Set();
      (Array.isArray(items) ? items : []).forEach((it) => {
        const id = it && it.upload_id ? String(it.upload_id).trim() : '';
        if (id) next.add(id);
      });
      return next;
    });
  }, [items]);

  const openSelectedInGraph = useCallback(() => {
    const ids = Array.from(selectedUploadIds);
    if (!ids.length) return;
    const qs = new URLSearchParams({ upload_ids: ids.join(',') }).toString();
    navigate(`/pcap?${qs}`);
  }, [navigate, selectedUploadIds]);

  const openSelectedInDetails = useCallback(() => {
    const ids = Array.from(selectedUploadIds);
    if (!ids.length) return;
    const first = ids[0];
    const qs = new URLSearchParams({ upload_ids: ids.join(',') }).toString();
    navigate(`/pcap/details/${encodeURIComponent(first)}?${qs}`);
  }, [navigate, selectedUploadIds]);

  const loadSelectedArtifacts = useCallback(async () => {
    const ids = Array.from(selectedUploadIds);
    if (!ids.length) return;
    setError('');
    setOkMsg('');

    setBulkArtifacts((prev) => {
      const next = { ...(prev || {}) };
      ids.forEach((id) => {
        next[id] = { ...(next[id] || {}), loading: true, error: '' };
      });
      return next;
    });

    try {
      await Promise.all(ids.map(async (id) => {
        const [stResp, thResp] = await Promise.all([
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/stories`, { withCredentials: true, timeout: 20000 }),
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/theses`, { withCredentials: true, timeout: 20000 }),
        ]);
        const stories = stResp && stResp.data && Array.isArray(stResp.data.items) ? stResp.data.items : [];
        const theses = thResp && thResp.data && Array.isArray(thResp.data.items) ? thResp.data.items : [];
        setBulkArtifacts((prev) => ({
          ...(prev || {}),
          [id]: { loading: false, error: '', stories, theses },
        }));
      }));
      setOkMsg('Loaded artifacts for selected uploads');
    } catch (e) {
      const msg = axiosErrMsg(e);
      setError(msg);
      setBulkArtifacts((prev) => {
        const next = { ...(prev || {}) };
        ids.forEach((id) => {
          if (!next[id]) next[id] = {};
          next[id] = { ...(next[id] || {}), loading: false, error: msg };
        });
        return next;
      });
    }
  }, [selectedUploadIds]);

  const deleteSelectedUploads = useCallback(async () => {
    const ids = Array.from(selectedUploadIds);
    if (!ids.length) return;

    const confirmed = window && typeof window.confirm === 'function'
      ? window.confirm(`Delete ${ids.length} upload(s)? This will remove their transactions/stories/theses too.`)
      : true;
    if (!confirmed) return;

    setDeleting(true);
    setError('');
    setOkMsg('');
    try {
      await axios.post(
        `${SERVER_NAME}/pcap/uploads/delete`,
        { upload_ids: ids },
        { withCredentials: true, timeout: 20000 }
      );
      setSelectedUploadIds(() => new Set());
      setBulkArtifacts(() => ({}));
      await loadUploads();
      setOkMsg(`Deleted ${ids.length} upload(s)`);
    } catch (e) {
      setError(axiosErrMsg(e));
    } finally {
      setDeleting(false);
    }
  }, [loadUploads, selectedUploadIds]);

  const readFileAsText = useCallback(async (file) => {
    return await new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e && e.target ? String(e.target.result || '') : '');
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      } catch (e) {
        reject(e);
      }
    });
  }, []);

  const csvToAnalysisV2 = useCallback((csvText) => {
    const raw = String(csvText || '');
    const lines = raw.split(/\r?\n/).filter((l) => String(l || '').trim() !== '');
    if (!lines.length) return '';

    const header = String(lines[0] || '').toLowerCase();
    if (header.includes('ts_start') && header.includes('src_ip') && header.includes('dst_ip')) {
      return raw;
    }

    const pick = (row, keys) => {
      if (!row || typeof row !== 'object') return '';
      for (const k of keys) {
        if (row[k] != null && String(row[k]).trim() !== '') return row[k];
      }
      return '';
    };

    const nowIso = new Date().toISOString();
    const out = [
      'ts_start,ts_end,duration_ms,src_ip,dst_ip,src_port,dst_port,protocol,packets,bytes,exporter,direction',
    ];

    let parsed = null;
    try {
      // eslint-disable-next-line global-require
      const Papa = require('papaparse');
      parsed = Papa.parse(raw, { header: true, skipEmptyLines: true });
    } catch (e) {
      parsed = null;
    }

    const rows = parsed && parsed.data && Array.isArray(parsed.data) ? parsed.data : [];
    for (const r of rows) {
      const srcIp = String(pick(r, ['src_ip', 'Src IP', 'Source IP', 'Client Addr', 'client_addr', 'client_ip', 'src', 'Src', 'source']) || '').trim();
      const dstIp = String(pick(r, ['dst_ip', 'Dst IP', 'Dest IP', 'Destination IP', 'Server Addr', 'server_addr', 'server_ip', 'dst', 'Dst', 'target']) || '').trim();
      if (!srcIp || !dstIp) continue;

      const srcPort = String(pick(r, ['src_port', 'Src Port', 'source_port', 'Client Port', 'client_port']) || '').trim();
      const dstPort = String(pick(r, ['dst_port', 'Dst Port', 'dest_port', 'Server Port', 'server_port']) || '').trim();
      const packets = String(pick(r, ['packets', 'Packets', 'pkts']) || '').trim();
      const clientBytes = Number(pick(r, ['Client Bytes', 'client_bytes', 'tx_bytes', 'Tx Bytes', 'tx']) || 0);
      const serverBytes = Number(pick(r, ['Server Bytes', 'server_bytes', 'rx_bytes', 'Rx Bytes', 'rx']) || 0);
      const bytes = Number.isFinite(clientBytes) || Number.isFinite(serverBytes)
        ? Math.max(0, Math.floor((Number.isFinite(clientBytes) ? clientBytes : 0) + (Number.isFinite(serverBytes) ? serverBytes : 0)))
        : Math.max(0, Math.floor(Number(pick(r, ['bytes', 'Bytes']) || 0)));

      const safe = (v) => {
        const s = String(v == null ? '' : v);
        if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      out.push([
        safe(nowIso),
        safe(nowIso),
        '0',
        safe(srcIp),
        safe(dstIp),
        safe(srcPort),
        safe(dstPort),
        '',
        safe(packets || '0'),
        String(bytes),
        'pcap_upload',
        'c2s',
      ].join(','));
    }

    return out.length > 1 ? `${out.join('\n')}\n` : '';
  }, []);

  const onUpload = useCallback(async (file) => {
    if (!file) return;
    setError('');
    setOkMsg('');

    if (file.size > plan.maxBytes) {
      setError(`File too large for plan. Max bytes: ${plan.maxBytes}`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let csvText = '';
      const name = file && file.name ? String(file.name) : '';
      const lower = name.toLowerCase();

      if (lower.endsWith('.pcap') || lower.endsWith('.pcapng')) {
        const fd = new FormData();
        fd.append('file', file);
        const convResp = await axios.post(`${SERVER_NAME}/openPCAP`, fd, {
          responseType: 'text',
          timeout: 300000,
        });
        csvText = convResp && typeof convResp.data === 'string' ? convResp.data : '';
      } else {
        csvText = await readFileAsText(file);
      }

      const v2 = csvToAnalysisV2(csvText);
      if (!v2) {
        throw new Error('No valid rows found');
      }

      const payloadName = (() => {
        const n = String(name || '').trim();
        const l = n.toLowerCase();
        if (!n) return 'upload.csv';
        if (l.endsWith('.pcap') || l.endsWith('.pcapng')) {
          return `${n.replace(/\.(pcapng|pcap)$/i, '')}.csv`;
        }
        if (l.endsWith('.csv')) return n;
        return `${n}.csv`;
      })();

      const payloadFile = new File([v2], payloadName, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', payloadFile);

      const resp = await axios.post(`${SERVER_NAME}/pcap/uploads`, formData, {
        withCredentials: true,
        timeout: 300000,
        onUploadProgress: (evt) => {
          const loaded = evt && evt.loaded ? Number(evt.loaded) : 0;
          const total = evt && evt.total ? Number(evt.total) : 0;
          const pct = total ? Math.round((loaded * 100) / total) : 0;
          setUploadProgress(Number.isFinite(pct) ? pct : 0);
        },
      });

      const uploadId = resp && resp.data && resp.data.upload_id ? String(resp.data.upload_id) : '';
      const status = resp && resp.data && resp.data.status ? String(resp.data.status) : '';

      await loadUploads();

      if (uploadId) {
        setOkMsg(`Upload created: ${uploadId} (${status || 'ok'})`);
        navigate(`/pcap/details/${encodeURIComponent(uploadId)}`);
      } else {
        setOkMsg('Upload complete');
      }
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (() => {
          const parts = [];
          if (e && e.message) parts.push(String(e.message));
          if (e && e.code) parts.push(`code=${String(e.code)}`);
          if (e && e.config && e.config.url) parts.push(`url=${String(e.config.url)}`);
          return parts.length ? parts.join(' ') : String(e);
        })();
      setError(msg);
    } finally {
      setUploading(false);
    }
  }, [loadUploads, navigate, plan.maxBytes]);

  const renderAuthGate = () => {
    if (!authChecked) {
      return (
        <Paper className={classes.card}>
          <Typography className={classes.title}>PCAP Uploads</Typography>
          <Typography className={classes.subtle}>Checking authentication…</Typography>
        </Paper>
      );
    }

    if (isAuthenticated) return null;

    return (
      <Paper className={classes.card}>
        <Typography className={classes.title}>PCAP Uploads</Typography>
        <Typography className={classes.subtle}>You must be logged in to upload and view private results.</Typography>
        <div style={{ marginTop: 12 }}>
          <Button variant="contained" color="primary" onClick={promptLogin}>
            Login
          </Button>
        </div>
      </Paper>
    );
  };

  const filteredItems = useMemo(() => {
    const q = String(searchText || '').trim().toLowerCase();
    if (!q) return Array.isArray(items) ? items : [];
    return (Array.isArray(items) ? items : []).filter((it) => {
      const filename = it && it.filename != null ? String(it.filename).toLowerCase() : '';
      const uploadId = it && it.upload_id != null ? String(it.upload_id).toLowerCase() : '';
      const status = it && it.status != null ? String(it.status).toLowerCase() : '';
      return filename.includes(q) || uploadId.includes(q) || status.includes(q);
    });
  }, [items, searchText]);

  return (
    <div className={classes.root}>
      {renderAuthGate() || (
        <Paper className={classes.card}>
          <Typography className={classes.title}>PCAP Analysis Uploads</Typography>
          <Typography className={classes.subtle}>
            Tier: {tier} | Uploads this month: {usedThisMonth}/{plan.uploadsPerMonth} | Max file: {Math.round(plan.maxBytes / (1024 * 1024))}MB | Max rows: {plan.maxRows}
          </Typography>
          <Divider style={{ margin: '12px 0', background: 'rgba(255,255,255,0.12)' }} />

          <div className={classes.toolbarRow}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.pcap,.pcapng"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e && e.target && e.target.files ? e.target.files[0] : null;
                if (e && e.target) e.target.value = '';
                onUpload(f);
              }}
            />

            <div className={classes.toolbarLeft}>
              <TextField
                value={searchText}
                onChange={(e) => setSearchText(e && e.target ? e.target.value : '')}
                placeholder="Search uploads"
                className={sharedClasses.searchField}
                style={{ width: '100%', minWidth: 0 }}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ color: 'white', marginRight: 6 }} />,
                  endAdornment: searchText ? (
                    <IconButton
                      onClick={() => setSearchText('')}
                      className={sharedClasses.iconButtonStyle}
                      style={{ padding: 0 }}
                      aria-label="Clear search"
                    >
                      <ClearIcon style={{ color: 'white' }} />
                    </IconButton>
                  ) : null,
                }}
              />
            </div>

            <div className={classes.toolbarRight}>
              <IconButton
                onClick={openSelectedInGraph}
                disabled={loading || uploading || !selectedUploadIds.size}
                className={sharedClasses.iconButtonStyle}
                aria-label="Open merged graph"
              >
                <TimelineIcon />
              </IconButton>

              <IconButton
                onClick={openSelectedInDetails}
                disabled={loading || uploading || !selectedUploadIds.size}
                className={sharedClasses.iconButtonStyle}
                aria-label="Open merged details"
              >
                <InfoOutlinedIcon />
              </IconButton>

              <IconButton
                onClick={onPickFile}
                disabled={uploading}
                className={sharedClasses.iconButtonStyle}
                aria-label="Upload CSV / PCAP"
              >
                <AddIcon />
              </IconButton>

              <IconButton
                onClick={deleteSelectedUploads}
                disabled={uploading || loading || deleting || !selectedUploadIds.size}
                className={sharedClasses.iconButtonStyle}
                aria-label="Delete selected uploads"
              >
                <DeleteIcon style={{ color: selectedUploadIds.size ? '#fff' : 'rgba(255,255,255,0.4)' }} />
              </IconButton>

              <IconButton
                onClick={loadUploads}
                disabled={loading || uploading}
                className={sharedClasses.iconButtonStyle}
                aria-label="Refresh"
              >
                <span style={{ fontSize: 14 }}>↻</span>
              </IconButton>
            </div>

            {(loading || uploading) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 8 }}>
                <CircularProgress size={18} style={{ color: '#fff' }} />
                <Typography className={classes.subtle} style={{ color: '#fff' }}>
                  {uploading ? `Uploading… ${uploadProgress}%` : 'Loading…'}
                </Typography>
              </div>
            )}

            {deleting && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 8 }}>
                <CircularProgress size={18} style={{ color: '#fff' }} />
                <Typography className={classes.subtle} style={{ color: '#fff' }}>
                  Deleting…
                </Typography>
              </div>
            )}
          </div>

          {uploading && (
            <div style={{ marginTop: 10 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </div>
          )}

          {!!error && <Typography className={classes.error}>{error}</Typography>}
          {!!okMsg && <Typography className={classes.success}>{okMsg}</Typography>}

          <div style={{ marginTop: 10, height: 520 }}>
            <NodeList
              listVariant="uploads"
              uploadRows={filteredItems}
              selectedUploadIds={selectedUploadIds}
              onToggleUploadSelected={toggleSelected}
              onToggleAllUploadsSelected={toggleAllSelected}
              isMobile={false}
              isPortrait={false}
              showTokenViewToggle={false}
            />
          </div>

          {selectedUploadIds.size > 0 && (
            <div style={{ marginTop: 14 }}>
              <Typography className={classes.subtle}>
                Selected: <span className={classes.kv}>{Array.from(selectedUploadIds).join(', ')}</span>
              </Typography>
            </div>
          )}
        </Paper>
      )}
    </div>
  );
}
